import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { Radio, Users, Video, Star, UserPlus, UserCheck, ShoppingBag, Clock, Crown, Check } from "lucide-react";
import { api, money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function CreatorProfile() {
  const { email } = useParams();
  const nav = useNavigate();
  const { user, authApi } = useAuth();
  const [params, setParams] = useSearchParams();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("products");
  const [subscribing, setSubscribing] = useState(false);

  const load = useCallback(async () => {
    try {
      const cfg = user ? { headers: { Authorization: `Bearer ${localStorage.getItem("taven_user_token")}` } } : {};
      const { data } = await api.get(`/creators/${encodeURIComponent(email)}`, cfg);
      setP(data);
    } catch { setP(false); }
  }, [email, user]);
  useEffect(() => { load(); }, [load]);

  // Confirm subscription return from checkout.
  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("sub") === "success" && sid && user) {
      authApi.get(`/creators/subscription/status/${sid}`).then(({ data }) => {
        if (data.subscribed) toast.success("You're subscribed! 🎉");
        setParams({}); load();
      }).catch(() => {});
    }
  }, [params, user]);

  const follow = async () => {
    if (!user) { toast.error("Sign in to follow."); nav("/login", { state: { from: `/creator/${email}` } }); return; }
    try { const { data } = await authApi.post(`/live/host/${encodeURIComponent(email)}/follow`); setP((x) => ({ ...x, is_following: data.following, followers: data.followers })); }
    catch { toast.error("Could not update follow."); }
  };
  const subscribe = async () => {
    if (!user) { toast.error("Sign in to subscribe."); nav("/login", { state: { from: `/creator/${email}` } }); return; }
    setSubscribing(true);
    try {
      const { data } = await authApi.post(`/creators/${encodeURIComponent(email)}/subscribe`, { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      if (e?.response?.status === 409) toast.error("You're already subscribed.");
      else toast.error(e?.response?.data?.detail || "Could not start subscription.");
      setSubscribing(false);
    }
  };

  if (p === null) return <div className="py-24 text-center text-muted-foreground">Loading creator…</div>;
  if (p === false) return <div className="py-24 text-center text-muted-foreground">Creator not found.</div>;

  const isSelf = user && p && user.email && user.email.toLowerCase() === decodeURIComponent(email).toLowerCase();

  const TABS = [
    { k: "products", l: "Products", n: p.products.length },
    { k: "videos", l: "Videos", n: p.videos.length },
    { k: "lives", l: "Lives", n: p.live.length + p.upcoming.length },
    { k: "replays", l: "Replays", n: p.replays.length },
  ];

  return (
    <div data-testid="creator-profile">
      <div className="bg-taven-black text-white">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-10">
          <div className="flex items-start gap-5 flex-wrap">
            <span className="w-20 h-20 rounded-full bg-taven-gold text-black flex items-center justify-center text-3xl font-black font-display">{(p.name || "?")[0].toUpperCase()}</span>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl sm:text-3xl font-black font-display">{p.name}</h1>
                {p.is_live && <Link to={`/live/${p.live[0].id}`} className="flex items-center gap-1 text-[10px] font-bold uppercase bg-red-600 px-2 py-1 animate-pulse"><Radio className="w-3 h-3" /> Live</Link>}
              </div>
              <p className="text-sm text-white/60 capitalize mt-0.5">{p.role}</p>
              {p.bio && <p className="text-sm text-white/80 mt-2 max-w-xl">{p.bio}</p>}
              <div className="flex items-center gap-5 mt-3 text-sm">
                <span data-testid="profile-followers"><strong>{p.followers}</strong> <span className="text-white/50">Followers</span></span>
                <span data-testid="profile-subscribers"><strong>{p.subscribers}</strong> <span className="text-white/50">Subscribers</span></span>
              </div>
              <div className="flex items-center gap-2 mt-4">
                {isSelf ? (
                  <Link to="/studio" data-testid="profile-studio-btn" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2.5 bg-taven-gold text-black hover:bg-white">Creator Studio</Link>
                ) : (
                  <>
                  <button onClick={follow} data-testid="profile-follow-btn" className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2.5 transition-colors ${p.is_following ? "bg-taven-gold text-black" : "border border-white/30 text-white hover:border-taven-gold"}`}>{p.is_following ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}{p.is_following ? "Following" : "Follow"}</button>
                  {p.subscription_enabled && (
                    p.is_subscribed ? (
                      <span data-testid="profile-subscribed" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2.5 bg-taven-gold/20 text-taven-gold"><Check className="w-4 h-4" /> Subscribed</span>
                    ) : (
                      <button onClick={subscribe} disabled={subscribing} data-testid="profile-subscribe-btn" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2.5 bg-taven-orange text-white hover:bg-[#d9561a] disabled:opacity-60"><Crown className="w-4 h-4" /> Subscribe · {money(p.subscription_price)}/mo</button>
                    )
                  )}
                  </>
                )}
              </div>
              {p.subscription_enabled && p.benefits?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="profile-benefits">
                  {p.benefits.map((b, i) => <span key={i} className="flex items-center gap-1 text-[11px] bg-white/10 px-2.5 py-1 text-white/80"><Star className="w-3 h-3 text-taven-gold" /> {b}</span>)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-black/10 bg-white sticky top-0 z-20">
        <div className="mx-auto max-w-5xl px-4 md:px-8 flex gap-1">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`profile-tab-${t.k}`} className={`px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${tab === t.k ? "border-taven-gold text-black" : "border-transparent text-black/50 hover:text-black"}`}>{t.l} ({t.n})</button>
          ))}
        </div>
      </div>

      <section className="mx-auto max-w-5xl px-4 md:px-8 py-8">
        {tab === "products" && (
          p.products.length === 0 ? <Empty text="No products showcased yet." /> :
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {p.products.map((pr) => (
              <Link key={pr.handle} to={`/product/${pr.handle}`} data-testid={`profile-product-${pr.handle}`} className="border border-black/10 bg-white hover:border-taven-gold group">
                <div className="aspect-square bg-taven-gray overflow-hidden">{pr.image && <img src={pr.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
                <div className="p-3"><p className="text-sm font-medium line-clamp-2 group-hover:text-taven-gold">{pr.title}</p><p className="font-black font-display mt-1">{money(pr.price)}</p></div>
              </Link>
            ))}
          </div>
        )}
        {tab === "videos" && (
          p.videos.length === 0 ? <Empty text="No videos yet." /> :
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {p.videos.map((v) => <Link key={v.id} to={`/videos/${v.id}`} className="border border-black/10 bg-taven-black aspect-[9/16] relative overflow-hidden group">{v.thumbnail && <img src={v.thumbnail} alt="" className="w-full h-full object-cover opacity-80" />}<span className="absolute bottom-2 left-2 right-2 text-xs text-white font-bold line-clamp-2">{v.caption}</span></Link>)}
          </div>
        )}
        {tab === "lives" && (
          (p.live.length + p.upcoming.length) === 0 ? <Empty text="No lives right now." /> :
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...p.live, ...p.upcoming].map((s) => <LiveMini key={s.id} s={s} />)}
          </div>
        )}
        {tab === "replays" && (
          p.replays.length === 0 ? <Empty text="No replays yet." /> :
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {p.replays.map((s) => <LiveMini key={s.id} s={s} replay />)}
          </div>
        )}
      </section>
    </div>
  );
}

const Empty = ({ text }) => <p className="text-sm text-muted-foreground py-16 text-center">{text}</p>;

const LiveMini = ({ s, replay }) => (
  <Link to={`/live/${s.id}`} className="bg-white border border-black/5 hover:border-taven-gold group block">
    <div className="aspect-video bg-taven-black relative flex items-center justify-center overflow-hidden">
      {s.cover_image ? <img src={s.cover_image} alt="" className="w-full h-full object-cover opacity-80" /> : <Video className="w-9 h-9 text-taven-gold/60" />}
      <span className={`absolute top-2 left-2 flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 ${replay ? "bg-black/70 text-white" : s.status === "live" ? "bg-red-600 text-white" : "bg-taven-gold text-black"}`}>{replay ? "Replay" : s.status === "live" ? <><Radio className="w-3 h-3" /> Live</> : <><Clock className="w-3 h-3" /> Soon</>}</span>
    </div>
    <div className="p-3"><p className="font-bold text-sm line-clamp-1">{s.title}</p></div>
  </Link>
);
